import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Trash2, Phone, Mail, CalendarClock, UserPlus, CalendarPlus, Archive, MessageCircle, Send, Ban, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";

const waLink = (phone) => {
  let d = (phone || "").replace(/[^\d+]/g, "");
  if (d.startsWith("+")) d = d.slice(1);
  else if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = "31" + d.slice(1);
  return `https://wa.me/${d}`;
};

export default function Requests() {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [proposeRes, setProposeRes] = useState(null); // reservation for counter-proposal
  const [rejectRes, setRejectRes] = useState(null); // reservation to reject

  const load = () => {
    api.get("/admin/reservations").then((r) => setReservations(r.data));
    api.get("/admin/contacts").then((r) => setContacts(r.data));
  };
  useEffect(() => {
    load();
    api.get("/admin/treatments").then((r) => setTreatments(r.data.filter((t) => t.active !== false)));
  }, []);

  const markRes = async (r) => { await api.put(`/admin/reservations/${r.id}`, null, { params: { status: "handled" } }); toast.success("Verplaatst naar archief."); load(); };
  const unarchiveRes = async (r) => { await api.put(`/admin/reservations/${r.id}`, null, { params: { status: "new" } }); toast.success("Teruggezet."); load(); };
  const delRes = async (r) => { await api.delete(`/admin/reservations/${r.id}`); load(); };
  const markCon = async (c) => { await api.put(`/admin/contacts/${c.id}`, null, { params: { status: "handled" } }); toast.success("Verplaatst naar archief."); load(); };
  const unarchiveCon = async (c) => { await api.put(`/admin/contacts/${c.id}`, null, { params: { status: "new" } }); toast.success("Teruggezet."); load(); };
  const delCon = async (c) => { await api.delete(`/admin/contacts/${c.id}`); load(); };

  const toCustomer = async (r) => {
    try {
      const { data } = await api.post(`/admin/reservations/${r.id}/to-customer`);
      toast.success(data.created ? `Klant "${data.customer.name}" aangemaakt.` : `Bestaande klant "${data.customer.name}" gekoppeld.`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const newRes = reservations.filter((r) => r.status === "new" || r.status === "proposal_sent");
  const newCon = contacts.filter((c) => c.status === "new");
  const archived = [
    ...reservations.filter((r) => r.status !== "new" && r.status !== "proposal_sent").map((r) => ({ ...r, _type: "res" })),
    ...contacts.filter((c) => c.status !== "new").map((c) => ({ ...c, _type: "con" })),
  ].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));

  return (
    <div>
      <h1 className="text-3xl font-medium tracking-tight mb-6">Aanvragen</h1>
      <Tabs defaultValue="reserveringen">
        <TabsList className="bg-cream-subtle rounded-full mb-6 grid grid-cols-3 w-full h-auto p-1">
          <TabsTrigger value="reserveringen" data-testid="tab-reservations" className="rounded-full text-xs sm:text-sm px-1.5 py-1.5 whitespace-nowrap">Reserveringen ({newRes.length})</TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contacts" className="rounded-full text-xs sm:text-sm px-1.5 py-1.5 whitespace-nowrap">Berichten ({newCon.length})</TabsTrigger>
          <TabsTrigger value="archief" data-testid="tab-archive" className="rounded-full text-xs sm:text-sm px-1.5 py-1.5 whitespace-nowrap">Archief ({archived.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="reserveringen" className="space-y-3">
          {!newRes.length && <Empty text="Geen open reserveringsaanvragen." />}
          {newRes.map((r) => {
            const known = r.customer_id || r.existing_customer_id;
            return (
              <Card key={r.id} testid={`reservation-${r.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.name}</span>
                      {r.status === "proposal_sent"
                        ? <Badge className="bg-[#FBEBD2] text-[#9A5B12] hover:bg-[#FBEBD2] border border-dashed border-[#E0A24A]">Voorstel verstuurd</Badge>
                        : <Badge className="bg-brand-light text-brand hover:bg-brand-light">Nieuw</Badge>}
                      {known && <Badge variant="secondary" data-testid={`existing-customer-${r.id}`} className="bg-brand-light text-brand"><UserPlus className="w-3 h-3 mr-1" /> Bestaande klant</Badge>}
                      {r.day_max > 0 && (r.day_over || r.day_full) && (
                        <Badge data-testid={`load-badge-${r.id}`}
                          className={r.day_over
                            ? "bg-[#FBE3E3] text-[#B4232A] hover:bg-[#FBE3E3] border border-[#EFB9BC]"
                            : "bg-[#FBEBD2] text-[#9A5B12] hover:bg-[#FBEBD2] border border-[#E0A24A]"}>
                          <AlertTriangle className="w-3 h-3 mr-1" /> Belasting {r.day_load}/{r.day_max}{r.day_over ? " · overvol" : " · vol"}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-ink-muted mt-1 flex flex-wrap gap-x-4">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span>
                    </div>
                    <div className="text-sm mt-2">
                      <span className="text-ink-muted">Behandeling:</span> {r.treatment_name}
                      {r.preferred_time && <> · <span className="text-ink-muted">Voorkeur:</span> {r.preferred_time}</>}
                    </div>
                    {r.message && <p className="text-sm text-ink-muted mt-1 italic">"{r.message}"</p>}
                    <div className="text-xs text-ink-muted mt-2 flex items-center gap-1"><CalendarClock className="w-3 h-3" />{format(parseISO(r.created_at), "d MMM yyyy HH:mm", { locale: nl })}</div>
                  </div>
                  <div className="flex flex-row sm:flex-col sm:items-end gap-1 shrink-0 flex-wrap">
                    <Button size="sm" onClick={() => navigate(`/admin/agenda?reservation=${r.id}`)} data-testid={`schedule-res-${r.id}`} className="rounded-full text-xs h-8 bg-brand hover:bg-brand-hover text-white"><CalendarPlus className="w-3.5 h-3.5 mr-1" /> Inplannen</Button>
                    <Button size="sm" variant="outline" onClick={() => setProposeRes(r)} data-testid={`propose-res-${r.id}`} className="rounded-full text-xs h-8 border-[#E8E2D9]"><Send className="w-3.5 h-3.5 mr-1" /> Tegenvoorstel</Button>
                    <Button size="sm" variant="outline" onClick={() => setRejectRes(r)} data-testid={`reject-res-${r.id}`} className="rounded-full text-xs h-8 border-[#E8E2D9] text-destructive"><Ban className="w-3.5 h-3.5 mr-1" /> Afwijzen</Button>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => markRes(r)} data-testid={`handle-res-${r.id}`} title="Naar archief"><Archive className="w-4 h-4 text-ink-muted" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => delRes(r)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="contact" className="space-y-3">
          {!newCon.length && <Empty text="Geen open contactberichten." />}
          {newCon.map((c) => (
            <Card key={c.id} testid={`contact-msg-${c.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div>
                  <div className="flex items-center gap-2"><span className="font-medium">{c.name}</span><Badge className="bg-brand-light text-brand hover:bg-brand-light">Nieuw</Badge></div>
                  <div className="text-sm text-ink-muted mt-1 flex flex-wrap gap-x-4">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>
                  </div>
                  <p className="text-sm mt-2">{c.message}</p>
                  <div className="text-xs text-ink-muted mt-2 flex items-center gap-1"><CalendarClock className="w-3 h-3" />{format(parseISO(c.created_at), "d MMM yyyy HH:mm", { locale: nl })}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" asChild data-testid={`email-con-${c.id}`} className="rounded-full text-xs h-8 border-[#E8E2D9]">
                    <a href={`mailto:${c.email}?subject=${encodeURIComponent("Re: je bericht aan Nelleke Holt")}`}><Mail className="w-3.5 h-3.5 mr-1" /> E-mail</a>
                  </Button>
                  <Button size="sm" variant="outline" asChild data-testid={`whatsapp-con-${c.id}`} className="rounded-full text-xs h-8 border-[#E8E2D9] text-[#128C7E]">
                    <a href={waLink(c.phone)} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp</a>
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => markCon(c)} data-testid={`handle-con-${c.id}`} title="Naar archief"><Archive className="w-4 h-4 text-ink-muted" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => delCon(c)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="archief" className="space-y-3">
          {!archived.length && <Empty text="Archief is leeg." />}
          {archived.map((a) => (
            <Card key={`${a._type}-${a.id}`} testid={`archived-${a.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{a.name}</span>
                    <Badge variant="secondary">{a._type === "res" ? "Reservering" : "Contact"}</Badge>
                  </div>
                  <div className="text-sm text-ink-muted mt-1 flex flex-wrap gap-x-4">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{a.email}</span>
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.phone}</span>
                  </div>
                  {a._type === "res" && <div className="text-sm mt-2"><span className="text-ink-muted">Behandeling:</span> {a.treatment_name}</div>}
                  {a.message && <p className="text-sm text-ink-muted mt-1 italic">"{a.message}"</p>}
                  <div className="text-xs text-ink-muted mt-2 flex items-center gap-1"><CalendarClock className="w-3 h-3" />{format(parseISO(a.created_at), "d MMM yyyy HH:mm", { locale: nl })}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => a._type === "res" ? unarchiveRes(a) : unarchiveCon(a)} data-testid={`unarchive-${a.id}`} className="rounded-full text-xs h-8 border-[#E8E2D9]">Terugzetten</Button>
                  <Button size="icon" variant="ghost" onClick={() => a._type === "res" ? delRes(a) : delCon(a)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {proposeRes && (
        <ProposeDialog reservation={proposeRes} treatments={treatments}
          onClose={() => setProposeRes(null)} onDone={() => { setProposeRes(null); load(); }} />
      )}
      {rejectRes && (
        <RejectDialog reservation={rejectRes}
          onClose={() => setRejectRes(null)} onDone={() => { setRejectRes(null); load(); }} />
      )}
    </div>
  );
}

function ProposeDialog({ reservation, treatments, onClose, onDone }) {
  const reqStart = reservation.requested_start ? parseISO(reservation.requested_start) : null;
  const [treatmentId, setTreatmentId] = useState(reservation.treatment_id || "");
  const [date, setDate] = useState(reqStart ? format(reqStart, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"));
  const [time, setTime] = useState(reqStart ? format(reqStart, "HH:mm") : "10:00");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const sel = treatments.find((t) => t.id === treatmentId);

  const send = async () => {
    if (!treatmentId) { toast.error("Kies een behandeling."); return; }
    setSaving(true);
    try {
      await api.post(`/admin/reservations/${reservation.id}/propose`, {
        start: `${date}T${time}:00`, treatment_id: treatmentId, comment, notify: true,
      });
      toast.success("Tegenvoorstel verstuurd. De klant kan het accepteren.");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="propose-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Send className="w-5 h-5 text-brand" /> Tegenvoorstel doen</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl bg-cream-subtle/60 p-4 text-sm">
            <div className="font-medium">{reservation.name}</div>
            <div className="text-ink-muted">{reservation.email} · {reservation.phone}</div>
            {reservation.preferred_time && <div className="mt-1"><span className="text-ink-muted">Voorkeur klant:</span> {reservation.preferred_time}</div>}
          </div>
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Behandeling *</Label>
            <Select value={treatmentId} onValueChange={setTreatmentId}>
              <SelectTrigger className="rounded-xl border-[#E8E2D9]" data-testid="propose-treatment"><SelectValue placeholder="Kies een behandeling" /></SelectTrigger>
              <SelectContent>
                {treatments.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.duration_min} min</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm text-ink-muted mb-1.5 block">Datum *</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="propose-date" className="rounded-xl border-[#E8E2D9]" />
            </div>
            <div>
              <Label className="text-sm text-ink-muted mb-1.5 block">Tijd *</Label>
              <Input type="time" step="900" value={time} onChange={(e) => setTime(e.target.value)} data-testid="propose-time" className="rounded-xl border-[#E8E2D9]" />
            </div>
          </div>
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Opmerking voor de klant</Label>
            <Textarea data-testid="propose-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
              placeholder="Bijv. Dit tijdstip past helaas niet, zou dit moment schikken?" className="rounded-xl border-[#E8E2D9]" />
          </div>
          {sel && <p className="text-xs text-ink-muted">{sel.duration_min} min · € {Number(sel.price).toFixed(2)}. De klant moet minimaal 24 uur vooraf accepteren, anders vervalt het voorstel.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Annuleren</Button>
          <Button onClick={send} disabled={saving} data-testid="propose-send" className="rounded-full bg-brand hover:bg-brand-hover text-white">{saving ? "..." : "Voorstel versturen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ reservation, onClose, onDone }) {
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const send = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/reservations/${reservation.id}/reject`, { message });
      toast.success("Aanvraag afgewezen en verplaatst naar archief.");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-sm" data-testid="reject-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Ban className="w-5 h-5 text-destructive" /> Aanvraag afwijzen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-ink-muted">De klant ontvangt een bericht en de aanvraag gaat naar het archief.</p>
          <Textarea data-testid="reject-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
            placeholder="Bericht aan de klant (optioneel)" className="rounded-xl border-[#E8E2D9]" />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Annuleren</Button>
          <Button onClick={send} disabled={saving} data-testid="reject-send" className="rounded-full bg-destructive hover:bg-destructive/90 text-white">{saving ? "..." : "Afwijzen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Card = ({ children, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#E8E2D9] p-5 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">{children}</div>
);
const Empty = ({ text }) => <div className="bg-white rounded-2xl border border-[#E8E2D9] p-8 text-center text-ink-muted text-sm">{text}</div>;
